import { ScrollView, TableProps } from '@round/ui-kit';
import { getApplicationStyleVariables, VirtualizedTable } from '@round/whitelabel-ui';
import cn from 'classnames';
import styles from './PostsTable.module.css';
import { useState } from 'react';
import { useRectObserver } from 'Hooks/useRectObserver';
import { BasePostTableMeta } from '../../types';
import PostsNoDataLabel from 'Modules/Plans/Project/ProjectDetails/components/PostsNoDataLabel/PostsNoDataLabel';
import { BasePost } from './PostsTable';

// matches the 3.625rem row height PostsTable.module.css gives tbody cells
const ESTIMATED_ROW_HEIGHT_PX = 58;
const SKELETON_ROW_COUNT = 6;
// rows rendered beyond the visible window on each side, so fast scrolling paints ahead of the viewport.
// Every mounted row re-renders on each scroll step, so this trades scroll cost against that buffer.
const OVERSCAN_ROW_COUNT = 10;
// distance from the bottom at which the next page is requested, ~20 rows: a page is 50 posts plus
// its account data, so the request needs a longer runway than the 500px default gives it
const END_REACHED_THRESHOLD_PX = 1200;

type Props<T extends BasePost> = Omit<TableProps<T>, 'meta'> & {
    isLoading?: boolean;
    hasError?: boolean;
    meta: BasePostTableMeta<T>;
    onEndReached?: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
};

/**
 * PostsTable's virtualized sibling: same wrapper contract (rect-observed wrapper, absolutely
 * positioned scroll area, application style variables, no-data label, skeleton rows), but rows are
 * windowed and more are requested through onEndReached as the container scrolls.
 */
const VirtualizedPostsTable = <T extends BasePost>({
    isLoading,
    hasError,
    className,
    meta,
    data,
    onEndReached,
    hasNextPage,
    isFetchingNextPage,
    ...props
}: Props<T>) => {
    const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
    const rect = useRectObserver(containerRef);

    // skeletons are rendered in table cells,
    // so we need some rows to display skeletons on initial loading
    const initialLoading = !data.length && isLoading;
    const emptyRows = new Array(SKELETON_ROW_COUNT).fill({}) as T[];
    const rows = initialLoading ? emptyRows : data;

    const handleEndReached = () => {
        // hasNextPage stays true after a page fails, so without the error guard every scroll event
        // at the bottom edge would request that page again
        if (!hasNextPage || isFetchingNextPage || isLoading || hasError) {
            return;
        }

        onEndReached?.();
    };

    // Since nested tables (like this one) are rendered in a TD with full colspan, it's content dimensions affect the width of the parent table.
    // To avoid that, we are rendering the scrollContainer with absolute positioning. The wrapper div uses rect to ensure the table is rendered with its full height.
    return (
        <div style={{ height: rect?.height, position: 'relative' }}>
            <ScrollView className={styles.scrollView} ref={setContainerRef}>
                <VirtualizedTable
                    shouldDisplayFooter={!!data.length && !isLoading}
                    //in case of loading, we need a fallback row id
                    getRowId={(original, index) => original.id?.toString() ?? index.toString()}
                    className={cn(
                        styles.table,
                        styles.virtual,
                        getApplicationStyleVariables(meta.managedBy),
                        {
                            [styles.empty]: !data.length && !isLoading,
                        },
                        className
                    )}
                    containerClassName={styles.virtualContainer}
                    virtualizerOptions={{
                        count: rows.length,
                        estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
                        overscan: OVERSCAN_ROW_COUNT,
                    }}
                    onEndReached={handleEndReached}
                    endReachedThreshold={END_REACHED_THRESHOLD_PX}
                    isLoading={isFetchingNextPage}
                    noDataLabel={
                        <PostsNoDataLabel
                            hasError={!!hasError}
                            onAddPost={meta.onCreatePost}
                            onAddCreators={meta.onAddCreators}
                            isReadOnly={meta.managedBy !== 'creatorbase' || (!meta.onCreatePost && !meta.onAddCreators)}
                        />
                    }
                    data={rows}
                    meta={meta}
                    {...props}
                />
            </ScrollView>
        </div>
    );
};

export default VirtualizedPostsTable;
